/**
 * THIS FILE CONTAINS DEFINITIONS FOR DYNAMIC METRICS
 */

import { restrictValueToRange } from 'components/gauge/restrictValueToScale';
import { maxBy, sortBy } from 'lodash';
import { getStaticVisualisation } from './static-dials';
import {
  DynamicBrainFunctionVisualisation,
  RequiredVisualisationData,
  StaticBrainFunctionVisualisation
} from './types';

/**
 * Normalise a metric result
 *
 * Formula:
 * ```
 *  Given [a,b], where a > b or a < b
 *  Let `x` be a value within [a,b]
 *  [a, x, b] = [a-a, x-a, b-a] = [0, x-a, b-a]
 *  normalised_result = (x-a) / (b-a)
 *```
 * @param metric
 * @param args
 * @param getAtIndex The index of the metric results that should be used. If the metric does not have a value then the value resolves to 0
 * @returns
 */
export function normaliseMetric(
  metric: StaticBrainFunctionVisualisation,
  args: RequiredVisualisationData,
  getAtIndex = 0
): number {
  const sorted = sortBy(metric.getValues(args), _ => _.date.unix());
  const value = sorted.at(getAtIndex)?.value ?? 0;
  const out = normaliseValue(metric.scale, value);
  return out;
}

/**
 * Normalise a metric from [0,1] as a function of how much each segment has been filled
 *
 * @param scale
 * @param value
 * @returns
 */
export function normaliseValue(
  scale: StaticBrainFunctionVisualisation['scale'],
  value: number
) {
  // Get the first and last values of the scale
  const first = scale.at(0);
  const last = scale.at(-1);

  // Ensure the values exist (NOTE: This will usually never happen if the metrics are correctly configured)
  if (first === undefined || last === undefined) {
    throw new Error(
      `[normaliseValue] Expected first and last values of the scale to be defined. Got: [${first}, ${last}]`
    );
  }

  // Sanity check
  if (first === last) {
    throw new Error(
      `[normaliseValue] Expected first and last values of the scale to be different. Cannot continue because the range will be zero. Metric: ${JSON.stringify(
        scale
      )}`
    );
  }

  const ascendingSorted = sortBy(scale);
  const descendingSorted = sortBy(scale, _ => -_);

  if (
    JSON.stringify(scale) !== JSON.stringify(ascendingSorted) &&
    JSON.stringify(scale) !== JSON.stringify(descendingSorted)
  ) {
    throw new Error('The scale input is not sorted');
  }

  let sum = 0;

  /**
   * Calculate the fill in each segment
   */
  for (let i = 1; i < scale.length; i++) {
    const left = scale[i - 1];
    const right = scale[i];
    const restrictedValue = restrictValueToRange(value, left, right);

    // Calculate the relative location of `restrictedValue` in the range [left, right]
    const fillFactor = (restrictedValue - left) / (right - left);
    sum += fillFactor;
  }

  return sum / (scale.length - 1);
}

// eslint-disable-next-line
function normaliseScale(scale: StaticBrainFunctionVisualisation['scale']) {
  const first = scale.at(0);
  const last = scale.at(-1);
  if (first === undefined || last === undefined) {
    throw new Error(
      `[normaliseScale] Expected first and last values of the scale to be defined. Got: [${first}, ${last}]`
    );
  }

  if (first === last) {
    throw new Error(
      `[normaliseScale] Expected first and last values of the scale to be different. Cannot continue because the range will be zero. Metric: ${JSON.stringify(
        scale
      )}`
    );
  }

  return scale.map(val => (val - first) / (last - first));
}

export function findLargestMetric(
  visualisations: StaticBrainFunctionVisualisation[],
  args: RequiredVisualisationData
) {
  // Normalise each metric and find the largest one
  const maxMetric = maxBy(visualisations, metric =>
    normaliseMetric(metric, args)
  );
  if (maxMetric === undefined) {
    console.error(JSON.stringify({ visualisations, args }));
    throw new Error(
      `[findLargestMetric] Unexpected 'undefined' when finding the largest metric`
    );
  }
  return maxMetric;
}

function curryDynamicDialToGetVisualisationFn(
  metrics: StaticBrainFunctionVisualisation[]
) {
  return (
    args: RequiredVisualisationData
  ): ReturnType<DynamicBrainFunctionVisualisation['getVisualisation']> =>
    findLargestMetric(metrics, args);
}

function dynamicVisualisationBuilder(
  metrics: StaticBrainFunctionVisualisation[]
) {
  return {
    getVisualisation: curryDynamicDialToGetVisualisationFn(metrics),
    composition: metrics
  };
}

/**
 * [task-387](https://trello.com/c/tyWhBTP0/387)
 * Define "Vision/Oculomotor" dial to replace "Reaction Time" and "Visual Motor Processing Speed".
 */
const dynamic_visionOculomotorDial = dynamicVisualisationBuilder([
  getStaticVisualisation('rt'),
  getStaticVisualisation('vmps')
]);

const dynamic_moodDial = dynamicVisualisationBuilder([
  getStaticVisualisation('anxiety'),
  getStaticVisualisation('depression')
]);

export const DYNAMIC_VISUALISATIONS: Record<
  'vision/oculomotor' | 'mood',
  DynamicBrainFunctionVisualisation
> = {
  'vision/oculomotor': {
    type: 'dynamic',
    label: 'Vision/Oculomotor',
    ...dynamic_visionOculomotorDial
  },
  mood: {
    type: 'dynamic',
    label: 'Mood',
    ...dynamic_moodDial
  }
} as const;
